import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCustomers,
  fetchCustomerSites,
  fetchSites,
  fetchSiteScans,
  fetchScanResults,
  createCustomer,
  createSite,
  ScanResultsResponse,
} from '../services/api';
import { Customer, Site, ScanHistoryApiItem } from '../types/network';

// Query keys for consistent caching
export const queryKeys = {
  customers: ['customers'] as const,
  customerSites: (customerId: string) => ['customers', customerId, 'sites'] as const,
  sites: ['sites'] as const,
  siteScans: (siteId: string) => ['sites', siteId, 'scans'] as const,
  scanResults: (scanId: string) => ['scans', scanId, 'results'] as const,
};

// Hook to fetch all customers
export const useCustomers = () => {
  return useQuery({
    queryKey: queryKeys.customers,
    queryFn: fetchCustomers,
  });
};

// Hook to fetch sites for a specific customer
export const useCustomerSites = (customerId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.customerSites(customerId!),
    queryFn: () => fetchCustomerSites(customerId!),
    enabled: !!customerId, // Only run query if customerId exists
  });
};

// Hook to fetch all sites (legacy)
export const useSites = () => {
  return useQuery({
    queryKey: queryKeys.sites,
    queryFn: fetchSites,
  });
};

// Hook to fetch scans for a specific site
export const useSiteScans = (siteId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.siteScans(siteId!),
    queryFn: () => fetchSiteScans(siteId!),
    enabled: !!siteId,
  });
};

// Hook to fetch scan results
export const useScanResults = (scanId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.scanResults(scanId!),
    queryFn: () => fetchScanResults(scanId!),
    enabled: !!scanId,
  });
};

// Hook to create a customer
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (name: string) => createCustomer(name),
    onSuccess: () => {
      // Invalidate and refetch customers list
      queryClient.invalidateQueries({ queryKey: queryKeys.customers });
    },
  });
};

// Hook to create a site
export const useCreateSite = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ name, customerId }: { name: string; customerId?: string }) => 
      createSite(name, customerId),
    onSuccess: (newSite) => {
      // Invalidate sites list
      queryClient.invalidateQueries({ queryKey: queryKeys.sites });
      
      // If site has a customer, invalidate that customer's sites
      if (newSite.CustomerID) {
        queryClient.invalidateQueries({ 
          queryKey: queryKeys.customerSites(newSite.CustomerID) 
        });
      }
    },
  });
};

// Hook to get a single customer from cache
export const useCustomerFromCache = (customerId: string | undefined) => {
  const queryClient = useQueryClient();
  
  if (!customerId) return null;
  
  const customers = queryClient.getQueryData<Customer[]>(queryKeys.customers);
  return customers?.find(c => c.ID === customerId) || null;
};

// Hook to get a single site from cache
export const useSiteFromCache = (customerId: string | undefined, siteId: string | undefined) => {
  const queryClient = useQueryClient();
  
  if (!customerId || !siteId) return null;
  
  const sites = queryClient.getQueryData<Site[]>(queryKeys.customerSites(customerId));
  return sites?.find(s => s.ID === siteId) || null;
};
